/**
 * RSE (CSR) consolidated score for the QHSE dashboard.
 *
 * Computes 4 sub-scores (0-100) and a weighted overall score :
 *
 *   1. CO2 / km        : how green is your fleet (electric, fuel/km)
 *   2. Green fleet %   : share of electric or low-emission vehicles
 *   3. Gender parity   : women / total drivers (target = 30 %)
 *   4. Wage ratio      : senior / junior pay spread (target ≤ 2.5x)
 *
 * Each sub-score is clamped to [0, 100]. The overall score is the
 * weighted average :
 *     overall = 0.30 × co2 + 0.30 × green + 0.20 × parity + 0.20 × wage
 *
 * From the overall, we derive an EcoVadis-style tier :
 *   < 35   : 'none'         (no badge)
 *   35-54  : 'bronze'
 *   55-74  : 'silver'
 *   75-100 : 'gold'
 *
 * Pure function — no side effects. Returns null when `state` is missing.
 *
 * 01/06/2026 — QHSE / RSE module foundation (Claire Maréchal perk).
 */

const ELECTRIC_MARKERS = ['ZFE', 'Image verte'];
const LOW_EMISSION_FUEL_PER_KM_THRESHOLD = 0.20; // L/km — anything below counts green

function isGreenVehicle(v) {
  if (!v) return false;
  const specs = v.specialties || [];
  if (specs.some(s => ELECTRIC_MARKERS.includes(s))) return true;
  // Fallback heuristic for hybrids / fuel-efficient diesels.
  const fuelPerKm = (v.dailyFuel || 0) / Math.max(1, v.kmPerDay || 200);
  return fuelPerKm <= LOW_EMISSION_FUEL_PER_KM_THRESHOLD;
}

function computeCo2Score(vehicles) {
  if (!vehicles || vehicles.length === 0) return 50; // neutral when no fleet
  // Average consumption normalised to a 0-100 scale where 0 L/km = 100,
  // and 0.6 L/km (big diesel) = 0.
  const avgFuelPerKm = vehicles.reduce((s, v) => {
    const km = Math.max(1, v.kmPerDay || 200);
    return s + (v.dailyFuel || 0) / km;
  }, 0) / vehicles.length;
  const score = 100 - (avgFuelPerKm / 0.6) * 100;
  return Math.max(0, Math.min(100, Math.round(score)));
}

function computeGreenFleetScore(vehicles) {
  if (!vehicles || vehicles.length === 0) return 50;
  const green = vehicles.filter(isGreenVehicle).length;
  const pct = green / vehicles.length;
  // 30 % green → 100 score. Linear below.
  return Math.max(0, Math.min(100, Math.round((pct / 0.30) * 100)));
}

function computeParityScore(drivers) {
  if (!drivers || drivers.length === 0) return 50;
  // Drivers store a `gender` field ('M' / 'F'). Default to neutral fallback.
  const women = drivers.filter(d => d.gender === 'F').length;
  const pct = women / drivers.length;
  // 30 % women → 100. Industry baseline ~5 %; transport is male-dominated.
  return Math.max(0, Math.min(100, Math.round((pct / 0.30) * 100)));
}

function computeWageScore(drivers) {
  if (!drivers || drivers.length < 2) return 80; // not enough sample — generous default
  const wages = drivers
    .map(d => (typeof d.monthlyCost === 'number' ? d.monthlyCost : d.salary) || 0)
    .filter(w => w > 0);
  if (wages.length < 2) return 80;
  const min = Math.min(...wages);
  const max = Math.max(...wages);
  const ratio = max / min;
  // Target ratio ≤ 2.5x → 100. At 5x → 0.
  if (ratio <= 2.5) return 100;
  if (ratio >= 5) return 0;
  const score = 100 - ((ratio - 2.5) / 2.5) * 100;
  return Math.round(score);
}

export function computeRseScore(state) {
  if (!state) return null;
  const vehicles = Array.isArray(state.vehicles) ? state.vehicles : [];
  const drivers = Array.isArray(state.drivers) ? state.drivers : [];

  const co2 = computeCo2Score(vehicles);
  const green = computeGreenFleetScore(vehicles);
  const parity = computeParityScore(drivers);
  const wage = computeWageScore(drivers);
  const overall = Math.round(0.30 * co2 + 0.30 * green + 0.20 * parity + 0.20 * wage);

  let tier;
  if (overall < 35) tier = 'none';
  else if (overall < 55) tier = 'bronze';
  else if (overall < 75) tier = 'silver';
  else tier = 'gold';

  return {
    overall,
    tier,
    sub: { co2, green, parity, wage },
    weights: { co2: 0.30, green: 0.30, parity: 0.20, wage: 0.20 },
    // Raw stats — useful for the dashboard captions.
    raw: {
      vehiclesTotal: vehicles.length,
      greenVehiclesCount: vehicles.filter(isGreenVehicle).length,
      driversTotal: drivers.length,
      womenDrivers: drivers.filter(d => d.gender === 'F').length,
    },
  };
}

export const TIER_COLORS = {
  none:   { bg: 'bg-slate-700/40',  text: 'text-slate-300',  border: 'border-slate-500/30',  label: 'Non noté' },
  bronze: { bg: 'bg-amber-700/20',  text: 'text-amber-300',  border: 'border-amber-600/30',  label: 'Bronze' },
  silver: { bg: 'bg-slate-300/20',  text: 'text-slate-200',  border: 'border-slate-300/40',  label: 'Silver' },
  gold:   { bg: 'bg-yellow-500/15', text: 'text-yellow-300', border: 'border-yellow-400/40', label: 'Gold' },
};
